/**
 * copy.js — Check a Payment (Revenue Validation Branch)
 * ---------------------------------------------------------------
 * English is the canonical/default language (Product OS ->
 * ROADMAP.md "Revenue Validation Branch", FEATURES.md
 * "Internationalisation"). This file is the single source of copy
 * for the whole page, looked up through `useCopy()` in `i18n.jsx`.
 *
 * Every other language object below is intentionally EMPTY. That is
 * not an oversight: FEATURES.md is explicit that "translation review
 * status" must not represent an unreviewed machine translation as
 * reviewed/production-ready. Rather than fabricate translations for
 * legal, payment and verdict copy that nobody has checked, this pass
 * ships the full i18n *architecture* (language list, lookup,
 * fallback, persistence) with English content only. Any lookup that
 * misses in a non-English dictionary silently falls back to English
 * — see `i18n.jsx` `t()` — so the UI never breaks, it just isn't
 * translated yet. `LANGUAGES[].reviewed` records this honestly so
 * the UI can show "not yet translated" rather than pretend.
 *
 * Filling these in is real, separately-scoped work: a professional
 * translation (or careful review of a machine one) for every key
 * below, for every language, with particular care over §12 (Terms)
 * and the verdict labels, before it can be marked `reviewed: true`.
 *
 * "NEXT ITERATION" pass — sourced statistics: the risk-carousel
 * statistic slides (carousel_card2..card6) cite real, named sources
 * (FBI IC3, UK Finance, Verizon DBIR) with figures checked via live
 * web search at the time of writing (September 2026) rather than the
 * "figure pending" placeholder the previous pass used — see each
 * caption for the exact source/year. If a figure is later found to be
 * stale or mis-cited, correct the caption rather than removing the
 * source attribution.
 */

export const LANGUAGES = [
  { code: "en", name: "English", reviewed: true },
  { code: "fr", name: "Français", reviewed: false },
  { code: "es", name: "Español", reviewed: false },
  { code: "de", name: "Deutsch", reviewed: false },
  { code: "it", name: "Italiano", reviewed: false },
  { code: "pt", name: "Português", reviewed: false },
  { code: "nl", name: "Nederlands", reviewed: false },
];

export const DEFAULT_LANGUAGE = "en";

const en = {
  // Mini nav
  nav_back_to_askmax: "AskMax",
  nav_title: "Check a Payment",

  // Hero
  hero_kicker: "Payment instructions can change",
  hero_headline_amount: "A payment request has changed?",
  hero_headline_question: "Are the new details really from the right person?",
  hero_subhead_1: "A new bank detail or urgent request? Pause.",
  hero_subhead_2: "Verify through a channel you already trust.",
  hero_proposition: "Get another pair of eyes before you pay.",
  hero_supporting: "Preview of the proposed payment-security service. No real enquiry or payment is processed here.",
  hero_cta_primary: "Explore service options",
  hero_cta_secondary: "How it works",

  // Authority / experience — three anchor words, not paragraphs.
  // "NEXT ITERATION": strengthened with a bold heading, a short list
  // and an explicit commercial transition line (task brief §6), while
  // the original Decades/Global/Thousands treatment is preserved
  // exactly as approved.
  authority_eyebrow: "Who's behind this",
  authority_word_1: "Decades",
  authority_desc_1: "of offensive and defensive cybersecurity experience.",
  authority_word_2: "Global",
  authority_desc_2: "experience working with businesses, organisations and governments around the world.",
  authority_word_3: "Thousands",
  authority_desc_3: "of real-world security cases.",
  // Real-iPhone QA polish: the list originally repeated the three-word
  // grid almost verbatim (an "international work with governments" item
  // duplicating authority_desc_2, a "thousands of cases" item duplicating
  // authority_desc_3) — trimmed to only the two genuinely additive
  // points, per "reduce/edit rather than add."
  authority_heading: "Built from decades inside security. Offensive. Defensive. Real world.",
  authority_list_1: "Building real security technology",
  authority_list_3: "Protecting organisations from external and internal threats",
  authority_transition: "That experience is focused on helping people and businesses understand what to check and what to do next.",

  // Who is Max? — radically reduced, no biography. Preserved verbatim
  // per the task brief — core concept and every quoted statement.
  whoismax_eyebrow: "Who is Max?",
  whoismax_lede: "Practical cybersecurity experience, explained clearly.",
  whoismax_lede_2:
    "Building security technology. Understanding attacks from both sides. Working with organisations and governments worldwide. Thousands of real-world security cases.",
  whoismax_lesson: "People don't need more cybersecurity noise.",
  whoismax_lesson_2: "They need clarity.",
  // Real-iPhone QA polish: a short label frames the three questions
  // below as the concrete output/benefit ("these are the three things
  // AskMax answers for me"), not three unrelated questions.
  whoismax_output_label: "What you get",
  whoismax_core_q1: "What do we think?",
  whoismax_core_q2: "Why?",
  whoismax_core_q3: "What should you do next?",
  whoismax_closing_1: "You don't need to understand cybersecurity.",
  whoismax_closing_2: "That's our job.",

  // Real-world continuity bridge — NEW section (task brief §7).
  // Hard guardrail: recovery is never guaranteed, and no quantities or
  // percentages are invented here.
  continuity_eyebrow: "Beyond the check",
  continuity_heading: "This isn't theoretical.",
  continuity_body_1: "A suspicious payment request or unexpected incident can leave anyone unsure what to do.",
  continuity_body_2: "If money has not moved, pause and verify the instructions independently.",
  continuity_body_3: "If money has already moved, contact your bank or payment provider immediately.",
  continuity_body_4: "Recovery depends on the circumstances and can never be guaranteed.",
  continuity_guardrail: "Every case is different. Recovery can never be guaranteed.",

  // Built for people with something to protect — NEW audience section
  // (task brief §9). Every example below is illustrative, not a real
  // case or claim of frequency.
  audience_eyebrow: "Who this is for",
  audience_title: "Built for people with something to protect",
  audience_intro: "This kind of risk doesn't only happen to big companies with big budgets.",
  audience_card1_title: "Small Businesses",
  audience_card1_b1: "A supplier invoice with new bank details",
  audience_card1_b2: "An urgent payment request that looks like it's from the boss",
  audience_card1_b3: "Concern about business email compromise",
  audience_card1_b4: "A security review before anything goes wrong",
  audience_card2_title: "Individuals & Families",
  audience_card2_b1: "An unexpected urgent transfer request",
  audience_card2_b2: "A message claiming to be a family emergency",
  audience_card2_b3: "A bank detail or payment request that feels wrong",
  audience_card2_b4: "A second opinion before a big purchase or transfer",
  audience_card3_title: "Everyday People",
  audience_card3_b1: "A text or call asking you to act fast",
  audience_card3_b2: "A request to move money “to keep it safe”",
  audience_card3_b3: "A link you're being pressured to pay through",
  audience_card3_b4: "Simply: something that doesn't feel right",
  audience_card4_title: "Family Offices & Advisers",
  audience_card4_b1: "Verifying a beneficiary or wire instruction before a transfer",
  audience_card4_b2: "An unusual instruction that arrived only by email",
  audience_card4_b3: "A second opinion before executing a significant payment on someone else's behalf",
  audience_card4_b4: "Protecting a principal who trusts you to catch this",
  audience_card5_title: "Cryptocurrency Holders & Investors",
  audience_card5_b1: "A request to move funds to a “new” wallet",
  audience_card5_b2: "An unfamiliar investment platform or exchange",
  audience_card5_b3: "Pressure to transfer quickly and quietly",
  audience_card5_b4: "Checking a project or counterparty before you send",
  audience_card6_title: "Professionals Who Protect Others",
  audience_card6_b1: "Accountants verifying a client's payment instruction",
  audience_card6_b2: "Bookkeepers spotting something unusual",
  audience_card6_b3: "Lawyers and advisers wanting a second opinion for a client",
  audience_card6_b4: "IT professionals and MSPs escalating a suspicious request",

  // Protection campaign image section — NEW (task brief §10). Copy is
  // exact, approved wording; the image is the extracted lower panel of
  // the approved composite photo (see ProtectionSection.jsx).
  protection_line1: "Serious protection shouldn't be reserved for the few.",
  protection_line2: "You get on with your life.",
  protection_line3: "We help you see what you might miss.",
  protection_line4: "Put decades of cybersecurity experience on your side.",

  // Risk carousel — one idea per slide, minimal copy. Expanded to 7
  // slides (task brief §11-14): 1 illustrative example, 5 sourced
  // statistics translated into personal scenarios (card6 is the
  // crypto-specific detailed narrative), 1 closing/CTA slide.
  carousel_eyebrow: "Why this matters",
  carousel_illustrative_flag: "Illustrative example — not a verified case",
  carousel_sourced_flag: "Sourced statistic",
  carousel_card1_amount: "One payment",
  carousel_card1_line1: "One familiar invoice.",
  carousel_card1_line2: "One changed bank detail.",
  carousel_card1_line3: "One payment sent to the wrong person.",
  carousel_card2_kicker: "Business email compromise",
  carousel_card2_stat: "$3.047B",
  carousel_card2_caption:
    "The FBI IC3 recorded $3,046,598,558 in reported 2025 Business Email Compromise losses across all complainants (24,768 complaints) — FBI IC3 2025 Annual Report.",
  carousel_card2_source: "Source: FBI IC3 2025 Annual Report",
  carousel_card2_source_url: "https://www.ic3.gov/AnnualReport/Reports/2025_IC3Report.pdf",
  carousel_card3_kicker: "Bank transfer fraud, UK",
  carousel_card3_stat: "£576.4M",
  carousel_card3_caption:
    "Reported UK authorised push payment (APP) fraud losses in 2025, 19% higher than 2024 — UK Finance, Annual Fraud Report 2026.",
  carousel_card3_source: "Source: UK Finance Annual Fraud Report 2026",
  carousel_card3_source_url: "https://www.ukfinance.org.uk/policy-and-guidance/reports-and-publications/annual-fraud-report-2026",
  carousel_card4_kicker: "How it actually happens",
  carousel_card4_stat: "60%",
  carousel_card4_caption:
    "of breaches in the 2025 report involved a human as a factor. Verizon's definition includes human actions beyond being deceived — 2025 Data Breach Investigations Report.",
  carousel_card4_source: "Source: Verizon 2025 Data Breach Investigations Report",
  carousel_card4_source_url: "https://www.verizon.com/business/resources/reports/2025-dbir-data-breach-investigations-report.pdf",
  carousel_card5_kicker: "The scale",
  carousel_card5_stat: "$20B+",
  carousel_card5_caption:
    "The FBI said losses reported to its Internet Crime Complaint Center surpassed $20 billion in 2025. This is reported IC3 loss data, not a count of all U.S. crime losses.",
  carousel_card5_source: "Source: FBI IC3 2025 Annual Report",
  carousel_card5_source_url: "https://www.ic3.gov/AnnualReport/Reports/2025_IC3Report.pdf",
  // Real-iPhone QA polish: trimmed from three narrative lines to two —
  // this slide had visibly more content than every other slide,
  // making the carousel's mobile height inconsistent between slides.
  // Meaning and the sourced figure/caption are preserved unchanged.
  carousel_card6_kicker: "Cryptocurrency",
  carousel_card6_stat: "$7.2B",
  carousel_card6_line1: "A message. A “new” wallet.",
  carousel_card6_line2: "“Move it quickly. Don't tell anyone.”",
  carousel_card6_caption:
    "The FBI IC3 reported $7.228 billion in losses from cryptocurrency investment fraud in 2025, its highest source of reported financial losses — 2025 Annual Report.",
  carousel_card6_source: "Source: FBI IC3 2025 Annual Report",
  carousel_card6_source_url: "https://www.ic3.gov/AnnualReport/Reports/2025_IC3Report.pdf",
  carousel_card7_title: "The good news?",
  carousel_card7_body: "Protecting yourself can be surprisingly simple.",
  carousel_card7_body_2: "Add another pair of eyes.",
  carousel_card7_closing1: "You don't need to understand cybersecurity.",
  carousel_card7_closing2: "That's our job.",
  carousel_card7_cta: "Get it checked",
  carousel_prev: "Previous",
  carousel_next: "Next",
  carousel_swipe_hint: "Swipe →",
  carousel_slide_count: "of",
  carousel_paused_hint: "Paused",

  // How it works — expanded from 3 to 4 steps (task brief §15).
  how_eyebrow: "How it works",
  how_title: "Four simple steps",
  how_step1_title: "Choose your service",
  how_step1_body:
    "Preview Payment Check, Urgent Payment Check, Payment Security Review, or Incident Response & Recovery Support — choose the option you would want to ask about.",
  how_step2_title: "Preview the payment step",
  how_step2_body: "No payment provider is connected and no money is taken.",
  how_step3_title: "Preview the information step",
  how_step3_body: "See the kinds of information a future service might ask for. Nothing entered or selected here is sent or saved.",
  how_step4_title: "Get a clear answer",
  how_step4_body: "An operational service would provide findings and next steps. No human review happens in this preview.",

  // ⚠️ SAMPLE CASE STUDIES — reduced to exactly two contained editorial
  // cases using the Product-Owner-approved photography (task brief
  // §19-22). Still illustrative sample cases, NOT real customer
  // histories, NOT testimonials, NOT verified outcomes — every card
  // renders a visible flag (see SampleCaseStudies.jsx) and this must
  // not reach public production without Stephen's explicit
  // replacement/approval of the actual cases used.
  case_studies_eyebrow: "Illustrative scenarios",
  case_studies_title: "A payment request worth pausing over.",
  case_studies_intro: "Fictional examples only. These are not real cases, customers, testimonials or outcomes.",
  case_studies_flag: "Illustrative sample case — not a real customer history",
  case_study_verdict_label: "Verdict",
  case_study_direction_label: "Direction",
  case_study_1_title: "The €12,400 supplier invoice",
  case_study_1_body:
    "A familiar supplier invoice arrived — same layout, same logo, same amount owed. But the bank account details had changed.",
  case_study_1_direction:
    "Don't pay. Verify the new bank details directly with the supplier, using a phone number you already have — not one from the email.",
  case_study_2_title: "“I need this paid before 4pm.”",
  case_study_2_body:
    "A message from the managing director asked for €6,800 to be sent urgently, before end of day. Urgent, and from someone trusted — exactly the pattern worth slowing down for.",
  case_study_2_direction: "Stop the payment. Verify the request directly with the person by phone, before doing anything else.",

  // Pricing — Urgent (€500) is the primary commercial product. A
  // fourth product, Incident Response & Recovery Support, addresses a
  // different customer state (money already moved) and is priced case
  // by case, never as a fixed checkout (task brief §24-27).
  pricing_eyebrow: "Choose a check",
  pricing_title: "Service options at a glance",
  product_standard_badge: "Standard",
  product_payment_check_name: "Payment Check",
  product_payment_check_price: "€250",
  product_payment_check_for: "Something doesn't feel right.",
  product_payment_check_cta: "Preview Payment Check — €250",
  product_urgent_name: "Urgent Payment Check",
  product_urgent_price: "€500",
  product_urgent_for: "Money is about to move.",
  product_urgent_badge: "Recommended",
  product_urgent_cta: "Preview Urgent Payment Check — €500",
  product_review_badge: "Premium",
  product_review_name: "Payment Security Review",
  product_review_price: "€1,500",
  product_review_for: "Don't wait for the suspicious invoice.",
  product_review_cta: "Ask about a Security Review",
  product_incident_badge: "Already been scammed?",
  product_incident_name: "Incident Response & Recovery Support",
  product_incident_price: "Assessed case by case",
  product_incident_for: "Money has already moved.",
  product_incident_cta: "Preview incident enquiry",
  broader_cta_text: "Need broader cybersecurity help?",
  talk_to_us: "Talk to us",

  // Payment Security Review — enquiry (not automatic checkout)
  enquiry_title: "Tell us about your business",
  enquiry_intro: "This preview does not send an enquiry or book work. If the service is operational, scope would be agreed before anything is booked.",
  enquiry_field_context: "What's prompting the interest?",
  enquiry_field_organisation: "Organisation name",
  enquiry_field_email: "Email address",
  enquiry_field_phone: "Phone number (optional)",
  enquiry_submit: "Complete preview — nothing will be sent",

  // Already Been Scammed — recovery/incident enquiry (NEW, task brief
  // §24-27). This is deliberately NOT the fixed-price demo payment
  // flow: money has already moved, so pricing is agreed after we
  // understand what happened, not paid upfront against a fixed sum.
  recovery_title: "Tell us what happened",
  recovery_intro:
    "This preview does not send an enquiry or start an investigation. If the service is operational, pricing would be discussed after the situation is understood. Every case is different, and recovery can never be guaranteed.",
  recovery_field_what_happened: "What happened?",
  recovery_field_when: "When did this happen?",
  recovery_field_amount: "Roughly how much money or cryptocurrency was involved? (optional)",
  recovery_field_organisation: "Organisation (optional)",
  recovery_field_email: "Email address",
  recovery_field_phone: "Phone number (optional)",
  recovery_submit: "Complete preview — nothing will be sent",
  recovery_guardrail: "Every case is different. Recovery can never be guaranteed.",

  // Not sure which one — now covers all four products.
  notsure_title: "Not sure which one?",
  notsure_body:
    "If money may be about to move, pause and verify the instructions independently. If money has already moved, contact your bank or payment provider immediately. This preview shows proposed service options but does not send an enquiry or book work.",

  // Terms
  terms_title: "Preview disclosure",
  terms_disclosure:
    "This is a service preview. No payment is taken, no enquiry or evidence is sent or saved, and no assessment is performed. Any future review would be based on agreed information and could not guarantee that a transaction or counterparty is safe.",
  terms_legal_review_flag: "Final terms and a privacy notice are not published. This preview is not a request for service.",
  terms_consent_label: "I understand this is a preview and nothing will be sent or charged.",
  // Payment-first journey: Terms leads to a single obvious action —
  // continuing to the (demo) payment step — not a price restatement.
  terms_continue_to_payment: "View payment demo — no payment taken",

  // Tell us who you are — customer-details step, collected before
  // payment with deliberately low friction (task brief §17). Journey:
  // IDENTIFY → TERMS → PAY → INTAKE.
  identify_title: "Tell us who you are",
  identify_intro: "This demonstration keeps details in this page only. Nothing is sent to AskMax or saved. Do not enter real personal information.",
  identify_field_name: "Full name",
  identify_field_email: "Email",
  identify_field_mobile: "Mobile number",
  identify_field_mobile_hint_required: "Required for Urgent Payment Check.",
  identify_field_mobile_hint_optional: "Optional in this preview; the number is not sent or used to contact you.",
  identify_field_company: "Company (optional)",
  identify_field_country: "Country",
  identify_selected_service_label: "Selected service",
  identify_continue: "Continue",

  // Demo payment — SAFE, PREVIEW-ONLY (task brief §16). No processor
  // is connected; card fields are fixed, non-editable demo values and
  // nothing entered here is transmitted, stored or sent to analytics.
  checkout_demo_badge: "Demo payment",
  checkout_demo_warning: "No payment will be taken",
  checkout_demo_explainer: "This shows how paying will work. No real payment provider is connected, and no money will be taken.",
  checkout_service_label: "Service",
  checkout_amount_label: "Amount",
  checkout_field_card_number: "Card number",
  checkout_field_expiry: "Expiry",
  checkout_field_cvc: "CVC",
  checkout_continue_cta: "Continue — demo only",
  checkout_trust_line: "Preview only · No payment, enquiry or human review is connected.",

  // Payment confirmed — transitional screen between the demo payment
  // and case intake, per the approved payment-first journey. Always
  // makes clear no real charge occurred.
  payment_confirmed_title_1: "Demo payment step complete.",
  payment_confirmed_title_2: "No money was charged.",
  payment_confirmed_body:
    "No money was charged. This preview does not send a service request or create a case.",
  payment_confirmed_demo_note: "Preview mode — no payment provider is connected in this environment, so no real payment was taken.",
  payment_confirmed_continue: "Continue",

  // Secure intake
  intake_title: "Tell us what's happening",
  intake_field_what_happened: "What happened?",
  intake_field_amount: "How much money is involved?",
  intake_field_sent_already: "Have you already sent any money?",
  intake_field_sent_yes: "Yes",
  intake_field_sent_no: "No",
  intake_field_urgency: "How soon do you need help?",
  intake_field_counterparty: "Who or what organisation are you dealing with?",
  intake_field_evidence: "Supporting information or evidence",
  intake_field_evidence_hint: "Screenshots, invoices, PDFs, emails or messages, payment/beneficiary details, suspicious links, or other supporting documents.",
  intake_field_evidence_never: "Never share online banking passwords, authentication codes or other login credentials — we will never ask for them.",
  intake_field_evidence_not_uploaded: "Files stay in this page for the preview. They are not uploaded or saved.",
  intake_field_evidence_add_more: "Add another file",
  intake_field_evidence_remove: "Remove",
  intake_field_evidence_error_type: "Only images and PDF files are accepted.",
  intake_field_evidence_error_size: "Files must be 15MB or smaller.",
  intake_field_call_request: "Would you request a call? (not sent in this preview)",
  intake_field_phone: "Phone number",
  intake_field_call_time: "Preferred call time",
  intake_call_language_note: "Call requests are not sent or arranged in this preview.",
  intake_submit: "Complete preview — nothing will be sent",
  intake_required: "Required",

  // Emergency triage
  emergency_title: "If money has already been sent, act now",
  emergency_body:
    "Contact your bank or payment provider immediately using a number or channel you already trust. Ask them to flag the payment and whether it can be recalled. Do not wait for an AskMax assessment.",
  emergency_no_guarantee: "A fast report may help, but recovery can never be guaranteed.",
  emergency_continue: "Return to the preview",

  // Confirmation
  confirmation_title: "Preview complete — nothing was sent.",
  confirmation_demo_note: "No enquiry was sent, no case was created, no investigator was contacted and no payment was taken. AskMax has not received your details.",
  confirmation_back: "Back to Check a Payment",

  // Language
  language_label: "Language",
  language_not_reviewed: "Not yet translated — showing English",

  // Referral / affiliate partner section. Enquiry only — no
  // commission rate is published here (PRICING.md: TBD Product Owner
  // decision) and no affiliate economics are invented.
  referral_eyebrow: "Partner with us",
  referral_title: "Know someone who might need our help?",
  referral_body:
    "If you work with businesses, or simply know someone who could benefit from a second pair of eyes before money moves, introduce them to us.",
  referral_body_2: "Referral terms are still being prepared. This preview does not create a partnership or promise a commission.",
  referral_supporting:
    "For accountants, bookkeepers, consultants, IT professionals, MSPs, lawyers, advisers, agencies and others who regularly work with businesses.",
  referral_cta: "Preview referral enquiry",
  referral_form_title: "Become a referral partner",
  referral_field_name: "Name",
  referral_field_email: "Email",
  referral_field_company: "Company / profession",
  referral_field_website: "Website (optional)",
  referral_field_network: "Tell us briefly about your network / who you work with",
  referral_submit: "Complete preview — nothing will be sent",
  referral_confirmation_title: "Preview complete — nothing was sent.",
  referral_confirmation_body: "No referral enquiry was sent and no partnership was created.",
  referral_demo_note: "Preview: no partner-enquiry destination is configured yet, so this wasn't actually sent anywhere.",
};

// Intentionally empty — see file header. Every key falls back to
// English (`en`) via `t()` in `i18n.jsx` until a reviewed
// translation is added here.
const fr = {};
const es = {};
const de = {};
const it = {};
const pt = {};
const nl = {};

export const COPY = { en, fr, es, de, it, pt, nl };
